import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";

import { NavigationContext } from "./NavigationContext";

import { useBreakpoint } from "shared/hooks/useBreakpoint";

const NavigationProvider = ({ children }) => {
  /*
   * ------------------------------------------------------------------
   * State
   * ------------------------------------------------------------------
   */
  const [activeMenu, setActiveMenu] = useState(null);
  const [viewStack, setViewStack] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hoveringNav = useRef(false);
  const hoveringMegaMenu = useRef(false);
  const closeTimer = useRef(null);
  const { isMobile, isDesktop, isNavigationMobile } = useBreakpoint();
  const [menuPosition, setMenuPosition] = useState({
    left: 0,
    width: 0,
  });

  /*
   * ------------------------------------------------------------------
   * MegaMenu
   * ------------------------------------------------------------------
   */

  const openMegaMenu = useCallback((menuId, element) => {
    const rect = element.getBoundingClientRect();

    setMenuPosition({
      left: rect.left,
      width: rect.width,
    });

    setActiveMenu(menuId);
  }, []);

  const closeMegaMenu = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }

    hoveringNav.current = false;
    hoveringMegaMenu.current = false;

    setActiveMenu(null);
  }, []);
  const cancelCloseMegaMenu = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const scheduleCloseMegaMenu = useCallback(() => {
    cancelCloseMegaMenu();

    closeTimer.current = setTimeout(() => {
      if (
        !hoveringNav.current &&
        !hoveringMegaMenu.current
      ) {
        closeMegaMenu();
      }
    }, 120);
  }, [cancelCloseMegaMenu, closeMegaMenu]);

  const enterNav = useCallback(() => {
    hoveringNav.current = true;
    cancelCloseMegaMenu();
  }, [cancelCloseMegaMenu]);

  const leaveNav = useCallback(() => {
    hoveringNav.current = false;
    scheduleCloseMegaMenu();
  }, [scheduleCloseMegaMenu]);

  const enterMegaMenu = useCallback(() => {
    hoveringMegaMenu.current = true;
    cancelCloseMegaMenu();
  }, [cancelCloseMegaMenu]);

  const leaveMegaMenu = useCallback(() => {
    hoveringMegaMenu.current = false;
    scheduleCloseMegaMenu();
  }, [scheduleCloseMegaMenu]);
  /*
   * ------------------------------------------------------------------
   * Mobile navigation
   * ------------------------------------------------------------------
   */

  const openMobileMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMenuOpen(false);
    setViewStack([]);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMenuOpen((prev) => {
      if (prev) {
        setViewStack([]);
      }
      return !prev;
    });
  }, []);

  /*
  * ------------------------------------------------------------------
  * Mobile view stack
  * ------------------------------------------------------------------
  */

  const pushView = useCallback((view) => {
    setViewStack((prev) => [...prev, view]);
  }, []);

  const popView = useCallback(() => {
    setViewStack((prev) => prev.slice(0, -1));
  }, []);

  const resetViewStack = useCallback(() => {
    setViewStack([]);
  }, []);

  const currentView = viewStack.at(-1) ?? null;

  const hasView = !!currentView;
  const viewDepth = viewStack.length;
  
  // Blokada przewijania po otwarciu menu
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    document.body.style.overflow = isMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen, isMobile]);
  // Po obróceniu telefonu menu się zamknie
  useEffect(() => {
    if (!isMobile) {
      closeMobileMenu();
    }
  }, [isMobile, closeMobileMenu]);

  // Zamknięcie przez ESC
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMegaMenu();
        closeMobileMenu();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMegaMenu, closeMobileMenu]);
  // cleanup timera
  useEffect(() => {
    return () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);
  /*
   * ------------------------------------------------------------------
   * Context
   * ------------------------------------------------------------------
   */

  const value = useMemo(
    () => ({
      isMobile,
      isDesktop,
      isNavigationMobile,

      activeMenu,
      menuPosition,
      hasMegaMenuOpen: activeMenu !== null,

      openMegaMenu,
      closeMegaMenu,

      isMenuOpen,

      openMobileMenu,
      closeMobileMenu,
      toggleMobileMenu,

      viewStack,
      currentView,
      hasView,
      viewDepth,
      pushView,
      popView,
      resetViewStack,
      enterNav,
      leaveNav,
      enterMegaMenu,
      leaveMegaMenu,
    }),
    [
      isMobile,
      isDesktop,
      isNavigationMobile,
      activeMenu,
      menuPosition,
      isMenuOpen,

      openMegaMenu,
      closeMegaMenu,

      openMobileMenu,
      closeMobileMenu,
      toggleMobileMenu,

      viewStack,
      currentView,
      hasView,
      viewDepth,
      pushView,
      popView,
      resetViewStack,
      enterNav,
      leaveNav,
      enterMegaMenu,
      leaveMegaMenu
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationProvider;